import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { ApiService } from '../apis/api.service';
import { EventShareService } from '../event-share.service';
import { PageServiceService } from '../page-service.service';
import {
  SideKickInterfaceTypeUiEnum,
  SideKickStateChangeReqUiPb,
} from 'src/app/_interfaces/sidekickTransaction';
import { PropertyUiPb } from 'src/app/com/staybloom/uiproto/property/propertyUiProto';
import {
  SidekickDeviceData,
  SidekickLoginEvent,
  SidekickConnectionEvent,
  SidekickUiState,
} from 'src/app/_models/sidekick-widget.models';

const INITIAL_STATE: SidekickUiState = {
  connectionState: 'idle',
  errorMessage: '',
  isLoggedIn: false,
  showFeedback: false,
  testConnection: false,
  buttonEnabled: true,
  lastScreenName: null,
  connectedDeviceName: '',
};

/**
 * Scoped to the UpdateWidgetComponent lifecycle via `providers` array.
 * Manages all Sidekick connection state and API interactions.
 * Separates business logic from presentation completely.
 */
@Injectable()
export class SidekickWidgetService {
  private stateSubject = new BehaviorSubject<SidekickUiState>({ ...INITIAL_STATE });
  readonly state$: Observable<SidekickUiState> = this.stateSubject.asObservable();

  private sidekickTempData: SidekickDeviceData | null = null;
  private subscriptions = new Subscription();

  constructor(
    private apiService: ApiService,
    private eventShareService: EventShareService,
    private pageService: PageServiceService,
  ) {}

  /**
   * Call from component ngOnInit.
   * - Checks localStorage for an existing connected device.
   * - If found, fetches its human-readable name via getSidekickDevices.
   * - Subscribes to EventShareService streams for login/connection events.
   */
  initialize(): void {
    const stored = localStorage.getItem('sidekickDevice');
    if (stored) {
      const device: SidekickDeviceData = JSON.parse(stored);
      this.patchState({ connectionState: 'connected' });
      this.resolveDeviceName(device.id);
    }

    this.subscriptions.add(
      this.eventShareService.getWidgetSuccessData().subscribe((data: any) => {
        if (data && typeof data === 'object' && Object.keys(data).length) {
          this.handleLoginResponse(data as SidekickLoginEvent);
        }
      }),
    );

    this.subscriptions.add(
      this.eventShareService.getWidgetConnected().subscribe((data: any) => {
        if (data && typeof data === 'object' && Object.keys(data).length) {
          this.handleConnectionResponse(data as SidekickConnectionEvent);
        }
      }),
    );
  }

  /**
   * Call from component ngOnDestroy to clean up subscriptions
   * and reset shared service state.
   */
  destroy(): void {
    this.subscriptions.unsubscribe();
    // Reset shared BehaviorSubjects so the next widget open starts clean.
    // This is a known limitation of EventShareService's design — ideally these
    // would be reset via dedicated methods rather than direct reassignment.
    this.eventShareService.widgetConnected = new BehaviorSubject('');
    this.eventShareService.widgetSuccessData = new BehaviorSubject('');
  }

  pushSidekickScreen(
    screenName?: SideKickInterfaceTypeUiEnum,
    reviewType?: string,
    propertyData?: PropertyUiPb,
  ): void {
    const resolvedScreen = screenName ?? this.stateSubject.value.lastScreenName;
    this.patchState({ buttonEnabled: false });

    const storedDevice = localStorage.getItem('sidekickDevice');
    const sidekickData: SidekickDeviceData = storedDevice
      ? JSON.parse(storedDevice)
      : this.sidekickTempData;

    if (!sidekickData) {
      this.patchState({ buttonEnabled: true });
      return;
    }
    const payload: SideKickStateChangeReqUiPb = {
      sideKickEvent: [
        {
          password: sidekickData.credentials.password,
          sideKickTransaction: {
            sideKickDeviceId: sidekickData.id,
            sideKickInterfaceTypeEnum: resolvedScreen,
            unitRef: { inventoryStoreId: sidekickData.inventoryStoreRef.inventoryStoreId },
          },
        },
      ],
    };

    this.enrichPayloadForScreen(payload, resolvedScreen, reviewType, propertyData);

    const sub = this.apiService.changeSidekickState(payload).subscribe({
      next: (response: any) => {
        if (response?.sideKickEvent?.[0]?.errorMessage) {
          this.patchState({
            connectionState: 'failure',
            errorMessage: response.sideKickEvent[0].errorMessage,
            testConnection: true,
            buttonEnabled: true,
            lastScreenName: resolvedScreen,
          });
        } else {
          if (this.sidekickTempData && !localStorage.getItem('sidekickDevice')) {
            localStorage.setItem('sidekickDevice', JSON.stringify(this.sidekickTempData));
          }
          this.patchState({
            connectionState: 'connected',
            testConnection: false,
            buttonEnabled: true,
          });
        }
        sub.unsubscribe();
      },
      error: () => {
        this.patchState({
          connectionState: 'failure',
          testConnection: true,
          buttonEnabled: true,
        });
        sub.unsubscribe();
      },
    });
  }

  disconnectDevice(): void {
    localStorage.removeItem('sidekickDevice');
    this.eventShareService.setWidgetConnected({ isConnected: false });
    this.stateSubject.next({ ...INITIAL_STATE });
  }

  retrySidekickConnect(): void {
    this.patchState({
      connectionState: 'idle',
      errorMessage: '',
      testConnection: false,
    });
  }

  toggleFeedback(): void {
    this.patchState({ showFeedback: !this.stateSubject.value.showFeedback });
  }

  /**
   * Calls the getSidekickDevices API (SIDEKICK_DEVICE?query=...) to look up
   * the human-readable skdName for a given device id. Updates connectedDeviceName
   * in state once resolved.
   */
  private resolveDeviceName(deviceId: string): void {
    this.pageService
      .getCurrentHotelInventoryId()
      .pipe(take(1))
      .subscribe((inventoryStoreId) => {
        if (!inventoryStoreId) return;
        this.apiService.getSidekickDevices({ inventoryStoreId }).subscribe({
          next: (response: any) => {
            const match = response?.sideKick?.find((d: any) => d.id === deviceId);
            const displayName = match?.skdName ? `${match.skdName} (${deviceId})` : deviceId;
            this.patchState({ connectedDeviceName: displayName });
          },
        });
      });
  }

  private handleLoginResponse(data: SidekickLoginEvent): void {
    const { connectionState } = this.stateSubject.value;
    if (connectionState === 'connected') return;

    if (data.sideKickData) {
      this.sidekickTempData = data.sideKickData;
    }

    if (data.isLoggedIn) {
      this.patchState({ isLoggedIn: true, connectionState: 'connecting' });
      this.eventShareService.setWidgetConnectionData(true);
    } else if ('isLoggedIn' in data) {
      this.patchState({
        connectionState: 'failure',
        errorMessage: 'Sidekick device failed to connect. Please check the password.',
        isLoggedIn: false,
      });
    }
  }

  private handleConnectionResponse(data: SidekickConnectionEvent): void {
    const { connectionState } = this.stateSubject.value;
    if (connectionState === 'connected') return;

    if (data.isConnected) {
      this.patchState({ connectionState: 'success' });
      setTimeout(() => {
        this.patchState({ connectionState: 'connected' });
      }, 3000);
    } else if ('isConnected' in data) {
      this.patchState({
        connectionState: 'failure',
        errorMessage:
          'Sidekick device unreachable. Please ensure sidekick device is open and logged in.',
        isLoggedIn: false,
        testConnection: true,
      });
    }
  }

  private enrichPayloadForScreen(
    payload: SideKickStateChangeReqUiPb,
    screenName: SideKickInterfaceTypeUiEnum,
    reviewType: string,
    propertyData: PropertyUiPb,
  ): void {
    if (!propertyData) return;

    if (screenName === SideKickInterfaceTypeUiEnum.BOOKING_DEFAULT) {
      payload.sideKickEvent[0].sideKickTransaction.bookingTransactionData = {
        allowPayAtHotel: propertyData.bookingWebsitePAH,
        bookingURL:
          propertyData.bookingWebsiteBookUrl +
          '?checkIn=D-0&checkOut=D-1' +
          (propertyData.bookingWebsitePAH ? '&pt=a' : '') +
          '&utm_campaign=walk-in',
      };
    }

    if (screenName === SideKickInterfaceTypeUiEnum.FRONT_DESK_ASSISTANT_FEEDBACK_EXTERNAL) {
      const feedbackUrl =
        reviewType === 'Google' ? propertyData.feedbackUrl : propertyData.feedbackUrl2;
      payload.sideKickEvent[0].sideKickTransaction.frontDeskAssistantTransactionData = {
        feedbackInfo: { feedbackExternalURL: feedbackUrl },
      };
    }
  }

  private patchState(partial: Partial<SidekickUiState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }
}
